import {
  OrderSelectedDetailActions as ModifyOrderSelectedDetailActions,
  ModifyOrderSelectedDetailActionTypes,
} from '@ordersnproducts/modules/new-modify-order/actions/order-detail.actions';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { BucketActions, BucketsActionTypes } from '../actions/bucket-actions/bucket.action';
import { ModifyOnlineOrderAction, ModifyOnlineOrderActionTypes } from '../actions/location-specifications.actions';
import { RecipeModifyFormValues } from '../models/form-values';
import {
  OrderBuilderActions,
  RESET_BUILDER,
} from '@ordersnproducts/modules/order-builder/actions/order-builder.actions';
import * as moment from 'moment';
import { Order } from '@cemex-core/cmx-api';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { UnitCode } from '../../../../../modules/order-builder/modules/location-specification/models/enums/units-code.enum';

export interface ModifyFormState {
  forms: RecipeModifyFormValues[];
  invoiceLoaded: boolean;
  specialInstructionsRequest: string;
  purchaseOrderRequest: string;
  contactPersonFormConfig: FieldConfig[];
  isProcessingForms: boolean;
  stopProcessingForm?: boolean;
  bucketsToCancel: RecipeModifyFormValues;
  isLoading?: boolean;
  resetTimeTo?: boolean;
}

export const initialState: ModifyFormState = {
  forms: [],
  invoiceLoaded: false,
  specialInstructionsRequest: '',
  purchaseOrderRequest: '',
  contactPersonFormConfig: undefined,
  isProcessingForms: false,
  bucketsToCancel: undefined,
  stopProcessingForm: false,
  isLoading: true,
  resetTimeTo: false,
};

export let formCounter = -1;

/**
 * Adds an item to the recipe
 */
const makeRecipeItem = (values: { [key: string]: any }, orderId?: number): RecipeModifyFormValues => {
  const id = ++formCounter;
  const result: RecipeModifyFormValues = {
    id: id,
    values: { ...values },
  };

  if (orderId) {
    result.orderId = orderId;
  }

  return result;
};

/**
 * Returns an item seq num for the given item
 * If it has anticipated invoice it will return it's already defined seq num
 * If normal created item, will enerate it
 * @param item
 * @param index
 */
const makeSeqNum = (item: RecipeModifyFormValues, index: number): number => {
  if (item.values[SpecFieldNames.CONTRACT_ANTICIPATED_INVOICE]) {
    return item.values[SpecFieldNames.CONTRACT_ANTICIPATED_INVOICE].itemCode;
  } else {
    return 10 * (index + 1);
  }
};

const loadAdditionalServices = (order: Order): { [key: string]: any } => {
  const additionalServices =
    order.orderProducts[0].itemProperties &&
    (order.orderProducts[0].itemProperties.additionalServices || order.orderProducts[0].itemProperties.additionalService)
      ? order.orderProducts[0].itemProperties.additionalServices || order.orderProducts[0].itemProperties.additionalService
      : [];
  const result: { [key: string]: any } = {};
  additionalServices.forEach(service => {
    result[`${service.additionalServiceCode}_${service.additionalServiceId}`] = true;
  });
  return result;
};

export function locationSpecificationFormReducer(
  state = initialState,
  action: ModifyOnlineOrderAction | BucketActions | ModifyOrderSelectedDetailActions | OrderBuilderActions,
): ModifyFormState {
  switch (action.type) {
    case RESET_BUILDER:
    case ModifyOnlineOrderActionTypes.RESET: {
      formCounter = -1;
      return {
        ...initialState,
      };
    }
    case ModifyOnlineOrderActionTypes.ADD_ITEM: {
      return {
        ...state,
        forms: [...state.forms, makeRecipeItem({})],
      };
    }
    case ModifyOnlineOrderActionTypes.DUPLICATE_ITEM: {
      const duplicatedItem = {
        id: formCounter,
        values: { ...action.payload, id: formCounter },
      };
      return {
        ...state,
        forms: [...state.forms, duplicatedItem],
      };
    }
    case ModifyOnlineOrderActionTypes.UPDATE_ITEM: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.recipeFromValues.id) {
            const quantity = action.payload.recipeFromValues.values[SpecFieldNames.QUANTITY];
            return {
              ...action.payload.recipeFromValues,
              values: {
                ...action.payload.recipeFromValues.values,
                [SpecFieldNames.QUANTITY]: quantity && typeof quantity === 'number' && +quantity.toFixed(6),
              },
              contracts: item.contracts,
              units: item.units,
              spacing: item.spacing,
              additionalServices: item.additionalServices,
              orderId: item.orderId,
              locked: item.locked,
              loadingInvoice: item.loadingInvoice,
              invokeDC: item.invokeDC,
              dirty: item.dirty,
              dcValidation: item.dcValidation,
              plants: item.plants,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.RESET_DATE_TIME: {
      const newFormState = state.forms;
      newFormState.forEach(form => {
        form.values.time = undefined;
        if (form.values.hasOwnProperty(SpecFieldNames.TIME_TO)) {
          form.values[SpecFieldNames.TIME_TO] = undefined;
        }
        form.dcValidation = undefined;
        form.locked = false;
      });

      return { ...state, forms: newFormState };
    }
    case BucketsActionTypes.ADD_BUCKETS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              orderId: action.payload.orderId,
              spacing: action.payload.buckets,
            };
          } else {
            return item;
          }
        }),
        isLoading: false,
      };
    }
    case BucketsActionTypes.SET_BUCKET: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.recipe.id) {
            return {
              ...item,
              spacing: item.spacing.map(load => {
                if (load.id === action.payload.bucket.id) {
                  load.values[SpecFieldNames.SPACING_TIME];
                  load.values[SpecFieldNames.SPACING_LOADS];
                  return load;
                } else {
                  return load;
                }
              }),
            };
          } else {
            return item;
          }
        }),
      };
    }
    case BucketsActionTypes.CLEAR_BUCKETS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              spacing: [],
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_ITEMS_SEQ_NUM: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          return { ...item, values: { ...item.values, [SpecFieldNames.ITEM_SEQ_NUM]: makeSeqNum(item, index) } };
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_INVOICE_LOADED: {
      return {
        ...state,
        invoiceLoaded: action.payload,
      };
    }
    case ModifyOnlineOrderActionTypes.RESET_ITEMS_SEQ_NUM: {
      return {
        ...state,
        forms: state.forms.map((item, index) => {
          return { ...item, values: { ...item.values, [SpecFieldNames.ITEM_SEQ_NUM]: undefined } };
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.REMOVE_ITEM: {
      return {
        ...state,
        forms: state.forms.filter(i => i.id !== action.payload),
      };
    }
    case ModifyOnlineOrderActionTypes.STORE_ITEM_CONTRACTS: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderId) {
            item.contracts = action.payload.contracts;
          }
          return item;
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.LOCK_ITEM: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              locked: action.payload.locked,
              loadingInvoice: action.payload.loadingInvoice,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_PROCESSING_FORM: {
      return {
        ...state,
        isProcessingForms: action.payload.locked,
      };
    }
    case ModifyOnlineOrderActionTypes.SET_FOR_DC_VALIDATION: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            if (item.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS) && action.payload.invokeDC) {
              item.values[SpecFieldNames.COUNTER_OFFERS] = undefined;
            }
            if (item.values.hasOwnProperty(SpecFieldNames.COUNTER_OFFERS_TO) && action.payload.invokeDC) {
              item.values[SpecFieldNames.COUNTER_OFFERS_TO] = undefined;
            }
            return {
              ...item,
              values: {
                ...item.values,
                [SpecFieldNames.TIME]: action.payload.resetTime ? undefined : item.values[SpecFieldNames.TIME],
                ...(item.values.hasOwnProperty(SpecFieldNames.TIME_TO) && {
                  [SpecFieldNames.TIME_TO]:
                    action.payload.resetTime || !item.values[SpecFieldNames.TIME_TO]
                      ? undefined
                      : item.values[SpecFieldNames.TIME_TO],
                }),
              },
              invokeDC: action.payload.invokeDC,
              dcValidation: action.payload.invokeDC ? undefined : item.dcValidation,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_FORM_VALID: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              values: {
                ...item.values,
              },
              isValid: action.payload.formValid,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_DIRTY: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId || action.payload.dirtyAll) {
            return {
              ...item,
              dirty: action.payload.dirtyAll ? true : action.payload.dirty,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_ERROR_ON_VALIDATION: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              hasError: action.payload.hasError,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_INITIAL_LOAD: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId || action.payload.isInitialLoadAll) {
            return {
              ...item,
              isInitialLoad: action.payload.isInitialLoad,
            };
          } else {
            return item;
          }
        }),
      };
    }
    case ModifyOnlineOrderActionTypes.SET_DC_VALIDATION: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              plant: _safe(() => action.payload.DCResponse.loads[0].plant),
              dcValidation: action.payload.DCResponse,
            };
          } else {
            return item;
          }
        }),
      };
    }

    case ModifyOnlineOrderActionTypes.RESET_JOBSITE_SELECTION: {
      const newFormState = state.forms;
      newFormState.forEach(form => {
        form.values.unit = undefined;
        form.values.productSelect = undefined;
        form.values.contractSelect = undefined;
        if (form.orderId) {
          form.invokeDC = true;
          form.spacing = [];
          form.dcValidation = undefined;
        }
      });
      return { ...state, forms: newFormState };
    }

    case ModifyOrderSelectedDetailActionTypes.LOAD_ONLINE: {
      const contract = action.payload.orderProducts[0].hasOwnProperty('contract')
        ? {
            salesDocumentCode: action.payload.orderProducts[0].contract.contractCode,
            salesDocumentId: action.payload.orderProducts[0].contract.contractId,
          }
        : undefined;
      const orderProduct = action.payload.orderProducts[0];
      const customerUnit = orderProduct.customerUnit;
      const unit = {
        'unitId': _safe(() => customerUnit.unitId) || _safe(() => orderProduct.unitOfMeasureId),
        'unitCode': _safe(() => customerUnit.customerUnitCode) || _safe(() => orderProduct.unitOfMeasure),
        'unitDesc': _safe(() => customerUnit.customerUnitDesc) || _safe(() => orderProduct['unitOfMeasureDesc']),
      };
      const quantity = unit.unitCode === UnitCode.load ? _safe(() => action.payload['totalQuantityLoads']) : action.payload.totalQuantity;
      const values = {
        productSelect: action.payload.orderProducts[0].productId,
        catalog: false,
        quantity: quantity,
        unit: unit,
        contractSelect: contract,
        purchaseOrder: action.payload.purchaseOrder,
        additionalServices: loadAdditionalServices(action.payload),
        additionalInfo: action.payload.instructions,
        time: action.payload.programmedDateTime, // startOrderDateTime en la respuesta
        time_to: action.payload.orderProducts[0].deliveryDateTimeTo,
        date: moment(action.payload.programmedDateTime).toDate(), // startOrderDateTime
        isModify: true,
      };

      return {
        ...state,
        forms: [makeRecipeItem(values, action.payload.orderRequestId)],
      };
    }

    case ModifyOnlineOrderActionTypes.SET_RECEIVING_PERSON:
      state.forms.forEach(form => {
        if (form.locationConfig) {
          form.locationConfig.receivingPerson.selectedContactPersonId = action.payload;
        }
      });
      return state;

    case ModifyOnlineOrderActionTypes.SET_PURCHASE_ORDER_REQUEST: {
      return {
        ...state,
        purchaseOrderRequest: action.payload,
      };
    }

    case ModifyOnlineOrderActionTypes.SET_SPECIAL_INSTRUCTIONS_REQUEST: {
      return {
        ...state,
        specialInstructionsRequest: action.payload,
      };
    }

    case ModifyOnlineOrderActionTypes.SET_CONTACT_PERSON_FORM_CONFIG: {
      return {
        ...state,
        contactPersonFormConfig: action.payload,
      };
    }

    case ModifyOnlineOrderActionTypes.FETCH_ITEM_CONTRACTS:
    default: {
      return state;
    }

    case ModifyOnlineOrderActionTypes.SET_ORDER_ID: {
      return {
        ...state,
        forms: state.forms.map(item => {
          if (item.id === action.payload.orderFormId) {
            return {
              ...item,
              orderId: action.payload.orderId,
            };
          } else {
            return item;
          }
        }),
      };
    }

    case ModifyOnlineOrderActionTypes.SET_CATALOG_FALSE: {
      state.forms.forEach(form => {
        form.values.catalog = false;
      });
      return {
        ...state,
      };
    }

    case ModifyOnlineOrderActionTypes.SET_BUCKETS_TO_CANCEL: {
      return {
        ...state,
        bucketsToCancel: action.payload.previousForm,
      };
    }

    case ModifyOnlineOrderActionTypes.SET_STOP_PROCESSING_FORM: {
      return {
        ...state,
        stopProcessingForm: action.payload.stopProcessingForm,
      };
    }

    case BucketsActionTypes.LOAD_BUCKETS: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case BucketsActionTypes.LOAD_BUCKETS_ERROR: {
      return {
        ...state,
        isLoading: false,
      };
    }

    case ModifyOnlineOrderActionTypes.RESET_TIME_TO: {
      return {
        ...state,
        resetTimeTo: action.payload,
      };
    }
  }
}
